// frontend/src/components/Charts/ComparisonOverlayChart.tsx
import {
  ComposedChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from 'recharts'
import { Box } from '@mui/material'
import type { WalkTimelineRow, WalkAbsoluteRow } from '~/api/endpoints/walks'
import { formatAxisTick, formatCompactNumber } from '~/util/numbers'
import { shortDateTick } from '~/util/date'
import { COMPARISON_PAIR } from '~/config/tenantPalette'
import { AXIS_TICK, GRID_STROKE, TOOLTIP_NUM_STYLE } from './chrome/chartStyle'

// Semantic current-vs-compare two-tone (spec §4.1) — deliberately the tenant
// COMPARISON_PAIR, not the user-customizable chipPalette (primary/compare is a
// fixed semantic, not a positional series identity).
const [PRIMARY_COLOR, COMPARE_COLOR] = COMPARISON_PAIR

export type ComparisonOverlayMode = 'period' | 'metric'

interface Props {
  primaryRows: WalkTimelineRow[]
  comparisonRows: WalkTimelineRow[]
  primaryLabel: string
  comparisonLabel: string
  mode: ComparisonOverlayMode
}

export interface OverlayPoint {
  time: string
  primary?: number
  compare?: number
}

/** Sum a walk result to one numeric total per time bucket (across dims/series), skipping NULL
 *  values (zero-denominator ratio buckets → gap, not 0). The overlay compares two metric totals. */
function totalByTime(rows: WalkTimelineRow[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const r of rows) {
    if (r.value == null) continue
    m.set(r.time, (m.get(r.time) ?? 0) + r.value)
  }
  return m
}

function sortedTimes(rows: WalkTimelineRow[]): string[] {
  return [...new Set(rows.map((r) => r.time))].sort((a, b) => a.localeCompare(b))
}

/** metric mode → align by absolute time (both series share the window).
 *  period mode → align by positional bucket index (overlay the prior window onto the primary's X);
 *  an unmatched primary bucket gets compare: undefined (a gap, not a wrong value). */
export function buildOverlayPoints(
  primaryRows: WalkTimelineRow[],
  comparisonRows: WalkTimelineRow[],
  mode: ComparisonOverlayMode,
): OverlayPoint[] {
  const primary = totalByTime(primaryRows)
  const compare = totalByTime(comparisonRows)
  if (mode === 'metric') {
    const times = [...new Set([...primary.keys(), ...compare.keys()])].sort((a, b) =>
      a.localeCompare(b),
    )
    return times.map((t) => ({ time: t, primary: primary.get(t), compare: compare.get(t) }))
  }
  const pTimes = sortedTimes(primaryRows)
  const cTimes = sortedTimes(comparisonRows)
  return pTimes.map((t, i) => {
    const ct = cTimes[i]
    return {
      time: t,
      primary: primary.get(t),
      compare: ct === undefined ? undefined : compare.get(ct),
    }
  })
}

export interface OverlayAxisLabel {
  value: string
  position: 'insideTopLeft' | 'insideTopRight'
  dy: number
  style: { textAnchor: 'start' | 'end' }
}

/** Per-axis labels for the comparison overlay (#12 multi-axis labels). Rendered as a HORIZONTAL
 *  caption at the top of each axis (a rotated label inside the right edge can't reliably clear the
 *  wide right-axis tick numbers). metric mode = dual axis (left = primary metric at top-left, right =
 *  comparison metric at top-right); period mode = one shared axis labeled with the (single) metric. */
export function overlayAxisLabels(
  mode: ComparisonOverlayMode,
  primaryLabel: string,
  comparisonLabel: string,
): { left: OverlayAxisLabel; right: OverlayAxisLabel | null } {
  const left: OverlayAxisLabel = {
    value: primaryLabel,
    position: 'insideTopLeft',
    dy: -24,
    style: { textAnchor: 'start' },
  }
  if (mode === 'metric') {
    return {
      left,
      right: {
        value: comparisonLabel,
        position: 'insideTopRight',
        dy: -24,
        style: { textAnchor: 'end' },
      },
    }
  }
  return { left, right: null }
}

export function ComparisonOverlayChart({
  primaryRows,
  comparisonRows,
  primaryLabel,
  comparisonLabel,
  mode,
}: Props) {
  const points = buildOverlayPoints(primaryRows, comparisonRows, mode)
  // The right axis exists ONLY when comparing two different metrics (different scales).
  // Period mode shares one axis (same metric).
  const dualAxis = mode === 'metric'
  const axisLabels = overlayAxisLabels(mode, primaryLabel, comparisonLabel)

  return (
    <Box sx={{ width: '100%', height: 360 }}>
      <ResponsiveContainer>
        <ComposedChart data={points} margin={{ top: 40, left: 24, right: 24, bottom: 24 }}>
          <CartesianGrid stroke={GRID_STROKE} vertical={false} />
          <XAxis dataKey="time" tickFormatter={shortDateTick} tick={AXIS_TICK} />
          <YAxis
            yAxisId="left"
            tickFormatter={formatAxisTick}
            label={axisLabels.left}
            tick={AXIS_TICK}
          />
          {dualAxis ? (
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={formatAxisTick}
              label={axisLabels.right ?? undefined}
              tick={AXIS_TICK}
            />
          ) : null}
          <Tooltip
            formatter={(value) =>
              typeof value === 'number' ? formatCompactNumber(value) : String(value)
            }
            contentStyle={TOOLTIP_NUM_STYLE}
          />
          <Legend />
          <Line
            yAxisId="left"
            dataKey="primary"
            name={primaryLabel}
            type="monotone"
            stroke={PRIMARY_COLOR}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
          <Line
            yAxisId={dualAxis ? 'right' : 'left'}
            dataKey="compare"
            name={comparisonLabel}
            type="monotone"
            stroke={COMPARE_COLOR}
            strokeWidth={2}
            strokeDasharray="6 4"
            dot={false}
            isAnimationActive={false}
            connectNulls={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  )
}

export interface OverlayBar {
  dim: string
  primary?: number
  compare?: number
}

/** Sum an absolute walk result to one total per dim bucket, skipping NULL values. */
function totalByDim(rows: WalkAbsoluteRow[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const r of rows) {
    if (r.value == null) continue
    const dim = r.dims ? Object.values(r.dims).join(' / ') : 'all'
    m.set(dim, (m.get(dim) ?? 0) + r.value)
  }
  return m
}

/** Grouped-bar overlay data: one row per dim bucket with primary + compare totals. */
export function buildOverlayBars(
  primaryRows: WalkAbsoluteRow[],
  comparisonRows: WalkAbsoluteRow[],
): OverlayBar[] {
  const primary = totalByDim(primaryRows)
  const compare = totalByDim(comparisonRows)
  const dims = [...new Set([...primary.keys(), ...compare.keys()])]
  return dims.map((dim) => ({ dim, primary: primary.get(dim), compare: compare.get(dim) }))
}

interface BarProps {
  primaryRows: WalkAbsoluteRow[]
  comparisonRows: WalkAbsoluteRow[]
  primaryLabel: string
  comparisonLabel: string
  mode: ComparisonOverlayMode
}

export function ComparisonOverlayBars({
  primaryRows,
  comparisonRows,
  primaryLabel,
  comparisonLabel,
  mode,
}: BarProps) {
  const data = buildOverlayBars(primaryRows, comparisonRows)
  const dualAxis = mode === 'metric'
  const axisLabels = overlayAxisLabels(mode, primaryLabel, comparisonLabel)
  return (
    <Box sx={{ width: '100%', height: 360 }}>
      <ResponsiveContainer>
        <BarChart data={data} margin={{ top: 40, left: 24, right: 24, bottom: 24 }}>
          <CartesianGrid stroke={GRID_STROKE} vertical={false} />
          <XAxis dataKey="dim" tick={AXIS_TICK} />
          <YAxis
            yAxisId="left"
            tickFormatter={formatAxisTick}
            label={axisLabels.left}
            tick={AXIS_TICK}
          />
          {dualAxis ? (
            <YAxis
              yAxisId="right"
              orientation="right"
              tickFormatter={formatAxisTick}
              label={axisLabels.right ?? undefined}
              tick={AXIS_TICK}
            />
          ) : null}
          <Tooltip
            formatter={(value) =>
              typeof value === 'number' ? formatCompactNumber(value) : String(value)
            }
            contentStyle={TOOLTIP_NUM_STYLE}
          />
          <Legend />
          <Bar yAxisId="left" dataKey="primary" name={primaryLabel} fill={PRIMARY_COLOR} />
          <Bar
            yAxisId={dualAxis ? 'right' : 'left'}
            dataKey="compare"
            name={comparisonLabel}
            fill={COMPARE_COLOR}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  )
}
